import { Transition } from "@headlessui/react"
import { Head, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsPasswordPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Password settings",
    href: settingsPasswordPath(),
  },
]

export default function Password() {
  const { data, setData, put, processing, errors, recentlySuccessful, reset } =
    useForm({
      password_challenge: "",
      password: "",
      password_confirmation: "",
    })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    put(settingsPasswordPath(), {
      preserveScroll: true,
      onSuccess: () => reset(),
      onError: () => reset(),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Update password"
            description="Ensure your account is using a long, random password to stay secure"
          />

          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="password_challenge">Current password</Label>

              <Input
                id="password_challenge"
                type="password"
                className="mt-1 block w-full"
                autoComplete="current-password"
                placeholder="Current password"
                value={data.password_challenge}
                onChange={(e) => setData("password_challenge", e.target.value)}
              />

              <InputError messages={errors.password_challenge} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password">New password</Label>

              <Input
                id="password"
                type="password"
                className="mt-1 block w-full"
                autoComplete="new-password"
                placeholder="New password"
                value={data.password}
                onChange={(e) => setData("password", e.target.value)}
              />

              <InputError messages={errors.password} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">Confirm password</Label>

              <Input
                id="password_confirmation"
                type="password"
                className="mt-1 block w-full"
                autoComplete="new-password"
                placeholder="Confirm password"
                value={data.password_confirmation}
                onChange={(e) =>
                  setData("password_confirmation", e.target.value)
                }
              />

              <InputError messages={errors.password_confirmation} />
            </div>

            <div className="flex items-center gap-4">
              <Button type="submit" disabled={processing}>
                Save password
              </Button>

              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">Saved</p>
              </Transition>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
